import { signOut } from "firebase/auth";

import { auth } from "@/lib/firebase-client";
import { productStore } from "@/lib/product-store";
import { resultsCacheStore } from "@/lib/results-cache-store";
import { searchMetadataStore } from "@/lib/search-metadata-store";

// Only digests and bounded flags cross tabs. No credentials or content belong here.
const REVOKED_PREFIX = "cosimo_revoked_session:";
const TAB_OWNER = "cosimo_browser_owner";
const SHARED_OWNER = "cosimo_browser_owner";
// Keep this in sessionStorage too: a sibling may replace the shared owner first.
const REGISTERED_OWNER = "cosimo_browser_registered_owner";
const CLEANUP_PENDING = "cosimo_browser_cleanup_pending";
export const ANALYTICS_BLOCKED = "cosimo_revoked_analytics";
export const BROWSER_ANALYTICS_CLEARED = "cosimo:browser-analytics-cleared";
export const BROWSER_SESSION_UNAVAILABLE = "cosimo:browser-session-unavailable";
export const ANALYTICS_GENERATION = "cosimo_analytics_generation";
export const BROWSER_SESSION_CLEARED = "cosimo:browser-session-cleared";
let clearing = false;
let unavailable = false;
let documentAnalyticsBlocked = false;
let loadedGeneration: string | null = null;
try {
  if (typeof window !== "undefined") loadedGeneration = localStorage.getItem(ANALYTICS_GENERATION);
} catch {
  unavailable = true;
}

function rotateAnalyticsGeneration(): void {
  documentAnalyticsBlocked = true;
  localStorage.setItem(ANALYTICS_BLOCKED, "1");
  const generation = Array.from(crypto.getRandomValues(new Uint32Array(4)), (value) =>
    value.toString(16).padStart(8, "0")
  ).join("");
  localStorage.setItem(ANALYTICS_GENERATION, generation);
  window.dispatchEvent(new Event(BROWSER_ANALYTICS_CLEARED));
}

export function failBrowserSession(): void {
  unavailable = true;
  documentAnalyticsBlocked = true;
  clearPersonalState(false);
  window.dispatchEvent(new Event(BROWSER_SESSION_UNAVAILABLE));
}

export class StaleBrowserSessionError extends Error {
  constructor() {
    super("Browser session is no longer available");
  }
}

export function isBrowserSessionClearing(): boolean {
  return clearing;
}

function navigateToPublicPage(): void {
  // Removing only a fragment can be same-document navigation. Cleanup needs a
  // new document even when fencing occurs on the public landing page itself.
  if (window.location.pathname === "/" && !window.location.search) {
    window.history.replaceState(null, "", "/");
    window.location.reload();
  } else window.location.replace("/");
}

export function isBrowserAnalyticsBlocked(): boolean {
  if (clearing || unavailable || documentAnalyticsBlocked) return true;
  try {
    const owner = typeof window === "undefined" ? null : sessionStorage.getItem(TAB_OWNER);
    return (
      typeof window === "undefined" ||
      !!(owner && localStorage.getItem(REVOKED_PREFIX + owner) === "1") ||
      localStorage.getItem(ANALYTICS_GENERATION) !== loadedGeneration ||
      localStorage.getItem(ANALYTICS_BLOCKED) === "1"
    );
  } catch {
    return true;
  }
}

export async function browserSubjectKey(uid: string): Promise<string> {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(uid));
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

const PERSONAL_KEYS = new Set([
  "chatContext",
  "chat-origin",
  "searchMetadata",
  "resultsCache",
  "langsmith_fields_cache",
  "cosimo_guest_user_id",
  "cosimo_pending_signup",
  "cosimo_pending_auth_redirect",
  "userGender",
  "product_cache",
]);
const PERSONAL_PREFIXES = [
  "chat-",
  "search-",
  "mlt-",
  "browse-menu-",
  "navbar-category-",
  "brand-filter-",
  "taxonomy-",
  "current-search-facets-",
  "results-new-",
  "product-",
];

function removePersonalKeys(storage: Storage, preserveAuthHandoff = false): void {
  const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
  for (const key of keys) {
    // Public taxonomy/menu documents are shared catalog data, not search navigation state.
    if (key === "browse-menu-cache" || key === "browse-menu-etag") continue;
    // These one-shot markers carry the current anonymous visitor through login.
    // A verified revocation or registered-account replacement still removes them.
    if (
      preserveAuthHandoff &&
      (key === "cosimo_pending_signup" || key === "cosimo_pending_auth_redirect")
    )
      continue;
    if (
      key &&
      (PERSONAL_KEYS.has(key) || PERSONAL_PREFIXES.some((prefix) => key.startsWith(prefix)))
    ) {
      storage.removeItem(key);
    }
  }
}

function clearPersonalState(shared: boolean, preserveAuthHandoff = false): void {
  // Independently attempt every store; one blocked storage API must not prevent sign-out.
  for (const clear of [
    () => resultsCacheStore.clear(),
    () => searchMetadataStore.clear(),
    () => removePersonalKeys(sessionStorage, preserveAuthHandoff),
    ...(shared
      ? [() => productStore.clear(), () => removePersonalKeys(localStorage, preserveAuthHandoff)]
      : []),
  ]) {
    try {
      clear();
    } catch {
      /* Storage unavailable: remote/offline cleanup is not certified. */
    }
  }
}

/** Check before exposing a restored UID to personal providers or analytics. */
export async function prepareBrowserSession(uid: string): Promise<boolean> {
  if (unavailable) {
    failBrowserSession();
    return false;
  }
  const key = await browserSubjectKey(uid);
  if (auth?.currentUser?.uid !== uid || clearing) return false;
  if (localStorage.getItem(REVOKED_PREFIX + key) === "1") {
    await revokeBrowserSession(uid);
    return false;
  }
  const previous = sessionStorage.getItem(TAB_OWNER);
  const sharedPrevious = localStorage.getItem(SHARED_OWNER);
  if ((previous && previous !== key) || (sharedPrevious && sharedPrevious !== key)) {
    const tabRegistered = sessionStorage.getItem(REGISTERED_OWNER);
    const registered = localStorage.getItem(REGISTERED_OWNER);
    if (
      (tabRegistered && tabRegistered !== key && tabRegistered === previous) ||
      (registered &&
        registered !== key &&
        (registered === previous || registered === sharedPrevious))
    ) {
      // A different registered account must never inherit the old analytics identity/queues,
      // including when the old account disappeared while this browser was offline.
      clearPersonalState(sharedPrevious !== key);
      sessionStorage.setItem(TAB_OWNER, key);
      localStorage.setItem(SHARED_OWNER, key);
      if (!auth?.currentUser?.isAnonymous) localStorage.setItem(REGISTERED_OWNER, key);
      if (!auth?.currentUser?.isAnonymous) sessionStorage.setItem(REGISTERED_OWNER, key);
      else sessionStorage.removeItem(REGISTERED_OWNER);
      rotateAnalyticsGeneration();
      window.dispatchEvent(new Event(BROWSER_SESSION_CLEARED));
      if (
        /^\/(results|chats?|profile|cart|collections|saved-searches|saved-brands|saved-products)(\/|$)/.test(
          window.location.pathname
        )
      )
        window.location.replace("/");
      else window.location.reload();
      return false;
    }
    clearPersonalState(sharedPrevious !== key, true);
  }
  sessionStorage.setItem(TAB_OWNER, key);
  localStorage.setItem(SHARED_OWNER, key);
  if (!auth?.currentUser?.isAnonymous) localStorage.setItem(REGISTERED_OWNER, key);
  if (!auth?.currentUser?.isAnonymous) sessionStorage.setItem(REGISTERED_OWNER, key);
  else sessionStorage.removeItem(REGISTERED_OWNER);
  if (localStorage.getItem(ANALYTICS_GENERATION) === loadedGeneration)
    localStorage.removeItem(ANALYTICS_BLOCKED);
  return true;
}

/** Only call for an independently verified terminal signal bound to this request's UID. */
export async function revokeBrowserSession(
  uid: string,
  reason: "subject_fenced" | "session_expired" = "subject_fenced"
): Promise<void> {
  if (!uid || (reason !== "subject_fenced" && auth?.currentUser?.uid !== uid)) return;
  let key: string;
  try {
    key = await browserSubjectKey(uid);
  } catch {
    if (auth?.currentUser?.uid === uid) {
      failBrowserSession();
      await signOut(auth);
    }
    return;
  }
  // This verified fence belongs to the request's UID even after a switch to B.
  // Remember A without gaining authority to clear or sign out the current B.
  if (reason === "subject_fenced") {
    try {
      localStorage.setItem(REVOKED_PREFIX + key, "1");
    } catch {
      /* Unavailable persistence is not certified as durable cleanup. */
    }
  }
  // Hashing yields; a different account may have signed in in the meantime.
  if (auth?.currentUser?.uid !== uid || clearing) return;
  clearing = true;
  try {
    sessionStorage.setItem(
      CLEANUP_PENDING,
      JSON.stringify({ owner: key, generation: loadedGeneration })
    );
    rotateAnalyticsGeneration();
  } catch {
    /* Still stop this reachable tab; durable storage cannot be certified. */
  }
  window.dispatchEvent(new Event(BROWSER_SESSION_CLEARED));
  const clearOriginalSession = () => {
    // Sign-out and page exit yield. Never clear a replacement account's newly owned state.
    if (auth?.currentUser?.uid && auth.currentUser.uid !== uid) return;
    let shared = false;
    try {
      const tabOwner = sessionStorage.getItem(TAB_OWNER);
      if (tabOwner && tabOwner !== key) return;
      const owner = localStorage.getItem(SHARED_OWNER);
      shared = !owner || owner === key;
      if (sessionStorage.getItem(REGISTERED_OWNER) === key)
        sessionStorage.removeItem(REGISTERED_OWNER);
      if (shared && localStorage.getItem(REGISTERED_OWNER) === key)
        localStorage.removeItem(REGISTERED_OWNER);
    } catch {
      /* Independently clear reachable tab state even when ownership storage is unavailable. */
    }
    clearPersonalState(shared);
  };
  clearOriginalSession();
  window.addEventListener("pagehide", clearOriginalSession, { once: true });
  // Calling signOut immediately after the UID check prevents delayed responses signing out B.
  try {
    if (auth?.currentUser?.uid === uid) await signOut(auth);
  } finally {
    // Destroy all provider state, pending callbacks, SDK queues and private route/query history.
    // A tombstone prevents restored Firebase credentials from reopening the old session.
    if (auth?.currentUser?.uid && auth.currentUser.uid !== uid) {
      // Preserve B's URL/storage and rebuild providers against B after the old cleanup.
      clearing = false;
      window.location.reload();
    } else {
      clearOriginalSession();
      navigateToPublicPage();
    }
  }
}

export function watchBrowserRevocation(): () => void {
  const check = () => {
    try {
      if (localStorage.getItem(ANALYTICS_GENERATION) !== loadedGeneration) {
        documentAnalyticsBlocked = true;
        window.dispatchEvent(new Event(BROWSER_ANALYTICS_CLEARED));
        const owner = sessionStorage.getItem(TAB_OWNER);
        if (owner && localStorage.getItem(REVOKED_PREFIX + owner) === "1") {
          sessionStorage.setItem(
            CLEANUP_PENDING,
            JSON.stringify({ owner, generation: loadedGeneration })
          );
          window.dispatchEvent(new Event(BROWSER_SESSION_CLEARED));
          clearPersonalState(localStorage.getItem(SHARED_OWNER) === owner);
          navigateToPublicPage();
        } else window.location.reload();
        return;
      }
    } catch {
      failBrowserSession();
      return;
    }
    const uid = auth?.currentUser?.uid;
    if (uid)
      void browserSubjectKey(uid)
        .then((key) => {
          if (localStorage.getItem(REVOKED_PREFIX + key) === "1") return revokeBrowserSession(uid);
        })
        .catch(() => {
          // Unavailable control storage must not expose restored personal state.
          failBrowserSession();
        });
  };
  const storage = (event: StorageEvent) => {
    if (event.key?.startsWith(REVOKED_PREFIX) || event.key === ANALYTICS_GENERATION) check();
  };
  window.addEventListener("storage", storage);
  window.addEventListener("pageshow", check);
  document.addEventListener("visibilitychange", check);
  return () => {
    window.removeEventListener("storage", storage);
    window.removeEventListener("pageshow", check);
    document.removeEventListener("visibilitychange", check);
  };
}

export function assertCurrentBrowserSession(uid: string | undefined): void {
  let oldDocument = false;
  if (typeof window !== "undefined") {
    try {
      oldDocument = localStorage.getItem(ANALYTICS_GENERATION) !== loadedGeneration;
    } catch {
      failBrowserSession();
      oldDocument = true;
    }
  }
  if (clearing || unavailable || oldDocument || (uid && auth?.currentUser?.uid !== uid))
    throw new StaleBrowserSessionError();
}

export async function checkBrowserSessionResponse(
  response: Response,
  uid?: string
): Promise<Response> {
  // Read a verified request-bound fence before rejecting a stale response. The
  // scoped tombstone survives, while revokeBrowserSession preserves current B.
  if (uid && response.status === 401) {
    const body = await response
      .clone()
      .json()
      .catch(() => null);
    if (body?.detail === "subject_fenced") {
      await revokeBrowserSession(uid);
      throw new StaleBrowserSessionError();
    }
  }
  assertCurrentBrowserSession(uid);
  return response;
}

// Close the crash/reload window before any provider can restore cached personal state.
// Modules importing the stores have already initialized their singleton maps, so clear both.
if (typeof window !== "undefined") {
  try {
    const pending = sessionStorage.getItem(CLEANUP_PENDING);
    if (pending) {
      let pendingOwner = pending;
      try {
        const value = JSON.parse(pending);
        pendingOwner = value.owner;
      } catch {
        /* Legacy marker has only the owner digest. */
      }
      const owner = localStorage.getItem(SHARED_OWNER);
      const tabOwner = sessionStorage.getItem(TAB_OWNER);
      if (!tabOwner || tabOwner === pendingOwner)
        clearPersonalState(!owner || owner === pendingOwner);
      sessionStorage.removeItem(CLEANUP_PENDING);
    }
  } catch {
    /* Auth readiness refuses unavailable control storage. */
  }
}
